import { useEffect, useState } from "react";
import { http, API } from "../api";
import { PageHeader, EmptyState } from "../shared";
import { Palette, Package, Save, Plus, Trash2, Upload, Check, X, Globe, LogIn, PackageOpen, Lock } from "lucide-react";

const DEFAULT_BRANDING = {
  brand_name: "Ward 7 CRM",
  brand_tagline_en: "Nabadwip · Ward 7 command center",
  brand_tagline_bn: "নবদ্বীপ · ওয়ার্ড ৭",
  logo_url: "",
  primary_color: "#0f766e",
  accent_color: "#ea823f",
  landing_hero_image: "",
  landing_mode: "public_blog",
};

const emptyFeature = () => ({ key: "", label: "", included: true, limit: "" });
const emptyPlan = () => ({
  name: "", price_monthly: 0, price_yearly: 0,
  features: [emptyFeature()], sort_order: 0, active: true,
});

// ---------------------------- White-labeling ------------------------------
function BrandingTab() {
  const [b, setB] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/branding");
      setB({ ...DEFAULT_BRANDING, ...(r.data || {}) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setToast("");
    try {
      await http.put("/branding", {
        brand_name: b.brand_name, brand_tagline_en: b.brand_tagline_en, brand_tagline_bn: b.brand_tagline_bn,
        logo_url: b.logo_url, primary_color: b.primary_color, accent_color: b.accent_color,
        landing_hero_image: b.landing_hero_image, landing_mode: b.landing_mode,
      });
      setToast("Saved · reload the app to see full effect");
      // Apply immediately
      document.documentElement.style.setProperty("--teal", b.primary_color);
      document.documentElement.style.setProperty("--amber", b.accent_color);
    } catch (e) {
      setToast(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post("/uploads/branding", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setB((prev) => ({ ...prev, logo_url: r.data.url }));
    } catch (e) {
      setToast(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  if (loading) return <div className="hint" style={{ padding: 24 }}>Loading branding…</div>;

  return (
    <div className="ss-panel" data-testid="ss-branding">
      <div className="ss-grid">
        <div className="ss-form">
          <label className="ss-field">
            <span>Brand name</span>
            <input
              data-testid="ss-brand-name"
              value={b.brand_name}
              onChange={(e) => setB({ ...b, brand_name: e.target.value })}
              placeholder="Ward 7 CRM"
            />
          </label>
          <div className="ss-two">
            <label className="ss-field">
              <span>Tagline (English)</span>
              <input
                data-testid="ss-tagline-en"
                value={b.brand_tagline_en}
                onChange={(e) => setB({ ...b, brand_tagline_en: e.target.value })}
              />
            </label>
            <label className="ss-field">
              <span>Tagline (বাংলা)</span>
              <input
                data-testid="ss-tagline-bn"
                value={b.brand_tagline_bn}
                onChange={(e) => setB({ ...b, brand_tagline_bn: e.target.value })}
                style={{ fontFamily: "'Noto Sans Bengali'" }}
              />
            </label>
          </div>

          <label className="ss-field">
            <span>Logo</span>
            <div className="ss-logo-row">
              {b.logo_url ? (
                <img src={b.logo_url.startsWith("http") ? b.logo_url : `${API.replace(/\/api$/, "")}${b.logo_url}`} alt="logo" className="ss-logo-preview" />
              ) : (
                <div className="ss-logo-blank">No logo</div>
              )}
              <div className="ss-logo-buttons">
                <label className="ss-upload-btn" data-testid="ss-logo-upload">
                  <Upload size={13} /> {uploading ? "Uploading…" : "Upload PNG/SVG"}
                  <input type="file" accept="image/*" hidden onChange={(e) => uploadLogo(e.target.files?.[0])} />
                </label>
                {b.logo_url && (
                  <button className="ss-btn-ghost" data-testid="ss-logo-clear" onClick={() => setB({ ...b, logo_url: "" })}>
                    <X size={12} /> Clear
                  </button>
                )}
              </div>
            </div>
          </label>

          <div className="ss-two">
            <label className="ss-field">
              <span>Primary color</span>
              <div className="ss-color-row">
                <input type="color" data-testid="ss-primary-color" value={b.primary_color} onChange={(e) => setB({ ...b, primary_color: e.target.value })} />
                <input type="text" value={b.primary_color} onChange={(e) => setB({ ...b, primary_color: e.target.value })} />
              </div>
            </label>
            <label className="ss-field">
              <span>Accent color</span>
              <div className="ss-color-row">
                <input type="color" data-testid="ss-accent-color" value={b.accent_color} onChange={(e) => setB({ ...b, accent_color: e.target.value })} />
                <input type="text" value={b.accent_color} onChange={(e) => setB({ ...b, accent_color: e.target.value })} />
              </div>
            </label>
          </div>

          <label className="ss-field">
            <span>Landing hero image URL (optional)</span>
            <input
              data-testid="ss-hero"
              value={b.landing_hero_image}
              onChange={(e) => setB({ ...b, landing_hero_image: e.target.value })}
              placeholder="https://…"
            />
          </label>

          <div className="ss-field">
            <span>Default page for anonymous visitors</span>
            <div className="ss-radio-row" data-testid="ss-landing-mode">
              <button
                type="button"
                className={`ss-radio ${b.landing_mode === "public_blog" ? "on" : ""}`}
                data-testid="ss-landing-mode-blog"
                onClick={() => setB({ ...b, landing_mode: "public_blog" })}
              >
                <Globe size={13} /> Public blog (current)
              </button>
              <button
                type="button"
                className={`ss-radio ${b.landing_mode === "login_default" ? "on" : ""}`}
                data-testid="ss-landing-mode-login"
                onClick={() => setB({ ...b, landing_mode: "login_default" })}
              >
                <LogIn size={13} /> Login screen
              </button>
            </div>
            <small className="ss-hint">
              {b.landing_mode === "public_blog"
                ? "Visitors first see the public advice / complaint landing page."
                : "Visitors are sent straight to the login screen."}
            </small>
          </div>

          <div className="ss-actions">
            <button className="primary" data-testid="ss-branding-save" onClick={save} disabled={saving}>
              <Save size={13} /> {saving ? "Saving…" : "Save branding"}
            </button>
            {toast && <small className="ss-toast" data-testid="ss-branding-toast">{toast}</small>}
          </div>
        </div>

        <div className="ss-preview" data-testid="ss-preview" style={{ "--pv-primary": b.primary_color, "--pv-accent": b.accent_color }}>
          <div className="ss-preview-title">Live preview</div>
          <div className="ss-preview-topbar">
            <div className="ss-preview-mark">
              {b.logo_url ? (
                <img src={b.logo_url.startsWith("http") ? b.logo_url : `${API.replace(/\/api$/, "")}${b.logo_url}`} alt="" />
              ) : (
                <span>{(b.brand_name || "W").slice(0, 2).toUpperCase()}</span>
              )}
            </div>
            <div>
              <b>{b.brand_name || "Brand"}</b>
              <small>{b.brand_tagline_en || "Tagline goes here"}</small>
            </div>
          </div>
          <div className="ss-preview-btn ss-preview-btn-primary">Primary action</div>
          <div className="ss-preview-btn ss-preview-btn-accent">Accent CTA</div>
          <div className="ss-preview-chip">Landing mode · <b>{b.landing_mode === "public_blog" ? "Public blog" : "Login screen"}</b></div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------- Plan Management -----------------------------
function PlanCard({ plan, onEdit, onDelete }) {
  return (
    <div className={`ss-plan-card ${plan.active ? "" : "off"}`} data-testid={`ss-plan-${plan.plan_id}`}>
      <div className="ss-plan-head">
        <b>{plan.name}</b>
        {!plan.active && <span className="ss-plan-inactive">Inactive</span>}
      </div>
      <div className="ss-plan-price">
        <span>₹{plan.price_monthly?.toLocaleString?.() ?? plan.price_monthly}</span>
        <em>/ month</em>
      </div>
      <small className="ss-plan-yearly">₹{plan.price_yearly?.toLocaleString?.() ?? plan.price_yearly} yearly</small>
      <ul className="ss-plan-features">
        {(plan.features || []).map((f, i) => (
          <li key={i} className={f.included ? "" : "off"}>
            {f.included ? <Check size={12} /> : <X size={12} />} {f.label}
            {f.limit ? <em> ({f.limit})</em> : null}
          </li>
        ))}
        {(plan.features || []).length === 0 && <li className="off">No features yet</li>}
      </ul>
      <div className="ss-plan-actions">
        <button className="ss-btn-ghost" data-testid={`ss-plan-edit-${plan.plan_id}`} onClick={() => onEdit(plan)}>Edit</button>
        {plan.active && (
          <button className="ss-btn-danger" data-testid={`ss-plan-delete-${plan.plan_id}`} onClick={() => onDelete(plan)}>
            <Trash2 size={11} /> Delete
          </button>
        )}
      </div>
    </div>
  );
}

function PlanModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || emptyPlan());
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.plan_id;

  const patch = (u) => setForm((f) => ({ ...f, ...u }));
  const patchFeature = (i, u) => setForm((f) => ({
    ...f,
    features: f.features.map((row, idx) => idx === i ? { ...row, ...u } : row),
  }));

  const save = async () => {
    if (!form.name.trim()) { window.alert("Plan name required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        price_monthly: Number(form.price_monthly) || 0,
        price_yearly: Number(form.price_yearly) || 0,
        features: (form.features || []).filter((f) => f.key.trim() || f.label.trim()).map((f) => ({
          key: (f.key || "").trim().toLowerCase().replace(/\s+/g, "_") || (f.label || "").toLowerCase().replace(/\s+/g, "_"),
          label: f.label.trim(),
          included: !!f.included,
          limit: f.limit || null,
        })),
        sort_order: Number(form.sort_order) || 0,
        active: !!form.active,
      };
      if (isEdit) {
        await http.patch(`/system/plans/${initial.plan_id}`, payload);
      } else {
        await http.post("/system/plans", payload);
      }
      onSaved();
    } catch (e) {
      window.alert(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal ss-plan-modal" data-testid="ss-plan-modal">
        <div className="modal-head">
          <h3>{isEdit ? "Edit plan" : "New plan"}</h3>
          <button className="icon-button" data-testid="ss-plan-modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body ss-form">
          <label className="ss-field">
            <span>Plan name</span>
            <input data-testid="ss-plan-name" value={form.name} onChange={(e) => patch({ name: e.target.value })} placeholder="Starter · Pro · Enterprise" />
          </label>
          <div className="ss-two">
            <label className="ss-field">
              <span>Price · monthly (INR)</span>
              <input type="number" data-testid="ss-plan-monthly" value={form.price_monthly} onChange={(e) => patch({ price_monthly: e.target.value })} />
            </label>
            <label className="ss-field">
              <span>Price · yearly (INR)</span>
              <input type="number" data-testid="ss-plan-yearly" value={form.price_yearly} onChange={(e) => patch({ price_yearly: e.target.value })} />
            </label>
          </div>

          <div className="ss-field">
            <span>Features</span>
            <div className="ss-feature-list" data-testid="ss-feature-list">
              {(form.features || []).map((f, i) => (
                <div key={i} className="ss-feature-row" data-testid={`ss-feature-row-${i}`}>
                  <input
                    placeholder="Label (e.g. Unlimited contacts)"
                    data-testid={`ss-feature-label-${i}`}
                    value={f.label}
                    onChange={(e) => patchFeature(i, { label: e.target.value })}
                  />
                  <input
                    placeholder="Limit (optional, e.g. 5,000)"
                    data-testid={`ss-feature-limit-${i}`}
                    value={f.limit || ""}
                    onChange={(e) => patchFeature(i, { limit: e.target.value })}
                  />
                  <button
                    type="button"
                    className={`ss-feature-toggle ${f.included ? "on" : ""}`}
                    data-testid={`ss-feature-toggle-${i}`}
                    onClick={() => patchFeature(i, { included: !f.included })}
                  >
                    {f.included ? <><Check size={11} /> On</> : <><X size={11} /> Off</>}
                  </button>
                  <button
                    type="button"
                    className="ss-btn-danger ss-feature-remove"
                    data-testid={`ss-feature-remove-${i}`}
                    onClick={() => patch({ features: form.features.filter((_, idx) => idx !== i) })}
                  >
                    <Trash2 size={10} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="ss-btn-ghost ss-feature-add"
                data-testid="ss-feature-add"
                onClick={() => patch({ features: [...(form.features || []), emptyFeature()] })}
              >
                <Plus size={11} /> Add feature
              </button>
            </div>
          </div>

          <div className="ss-two">
            <label className="ss-field">
              <span>Sort order</span>
              <input type="number" data-testid="ss-plan-sort" value={form.sort_order} onChange={(e) => patch({ sort_order: e.target.value })} />
            </label>
            <label className="ss-field ss-active-toggle">
              <span>Active</span>
              <button
                type="button"
                className={`ss-feature-toggle ${form.active ? "on" : ""}`}
                data-testid="ss-plan-active"
                onClick={() => patch({ active: !form.active })}
              >
                {form.active ? <><Check size={11} /> On</> : <><X size={11} /> Off</>}
              </button>
            </label>
          </div>
        </div>
        <div className="modal-foot">
          <button className="outline" onClick={onClose}>Cancel</button>
          <button className="primary" data-testid="ss-plan-save" onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get("/system/plans");
      setPlans(r.data.plans || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const deletePlan = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"? It will be marked inactive.`)) return;
    try {
      await http.delete(`/system/plans/${p.plan_id}`);
      await load();
    } catch (e) { window.alert(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="ss-panel" data-testid="ss-plans">
      <div className="ss-plans-head">
        <b>Subscription tiers</b>
        <button className="primary" data-testid="ss-plan-new" onClick={() => setCreating(true)}><Plus size={12} /> New plan</button>
      </div>
      {loading ? (
        <div className="hint" style={{ padding: 24 }}>Loading plans…</div>
      ) : plans.length === 0 ? (
        <EmptyState icon={PackageOpen} title="No subscription plans yet" hint='Click "New plan" to create your first tier.' />
      ) : (
        <div className="ss-plans-grid" data-testid="ss-plans-grid">
          {plans.map((p) => (
            <PlanCard key={p.plan_id} plan={p} onEdit={setEditing} onDelete={deletePlan} />
          ))}
        </div>
      )}
      {creating && (
        <PlanModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
      {editing && (
        <PlanModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

// ---------------------------- Root ----------------------------------------
export default function SystemSettings({ user }) {
  const isSuper = user?.role === "Super Admin";
  const [tab, setTab] = useState("branding");

  if (!isSuper) {
    return (
      <>
        <PageHeader eyebrow="SYSTEM" title="System settings" subtitle="This page is restricted to Super Admins." />
        <EmptyState icon={Lock} title="Access denied" hint="Ask a Super Admin to open this page for you." />
      </>
    );
  }

  return (
    <>
      <PageHeader eyebrow="SYSTEM" title="System settings"
        subtitle="Manage global branding, landing behaviour and subscription tiers." />
      <div className="ss-tabs" data-testid="ss-tabs">
        <button
          className={tab === "branding" ? "on" : ""}
          data-testid="ss-tab-branding"
          onClick={() => setTab("branding")}
        >
          <Palette size={13} /> White-labeling
        </button>
        <button
          className={tab === "plans" ? "on" : ""}
          data-testid="ss-tab-plans"
          onClick={() => setTab("plans")}
        >
          <Package size={13} /> Plan management
        </button>
      </div>
      {tab === "branding" ? <BrandingTab /> : <PlansTab />}
    </>
  );
}
